import { useEffect, useCallback, useMemo } from 'react';

import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { Session } from 'src/utils/session';

type MessageHandler<T> = () => Promise<T>;

type MenuInfo = {
  session: Session['data'];
  encodedSession: string;
  dispensaryId: string;
  dispensaryName: string;
  menuVariant: string;
};

type AdsInspectorRequest = {
  requestId: string;
  action: string;
  payload: any;
};

function useAttachToMenuHandler(): MessageHandler<MenuInfo> {
  const { variant: menuVariant } = useUI();
  const { dispensary } = useDispensary();

  const dispensaryId = dispensary?.id;
  const dispensaryName = dispensary?.name;

  return useCallback(
    () =>
      new Promise((resolve, reject) => {
        if (typeof dispensaryId !== 'string' || typeof menuVariant !== 'string') {
          reject(new Error('Dutchie menu is not ready'));
        } else {
          resolve({
            session: Session.getInstance().data,
            encodedSession: Session.getInstance().encoded,
            dispensaryId,
            dispensaryName,
            menuVariant,
          });
        }
      }),
    [dispensaryId, dispensaryName, menuVariant]
  );
}

export function AdsInspectorDevTool(): null {
  const attachToMenuHandler = useAttachToMenuHandler();

  const messageHandlerForAction = useMemo(
    () => ({
      attachToMenu: attachToMenuHandler,
    }),
    [attachToMenuHandler]
  );

  const eventHandler = useCallback(
    async (event: CustomEvent<AdsInspectorRequest>): Promise<void> => {
      const { requestId, action, payload } = event.detail;

      try {
        const messageHandler = messageHandlerForAction[action];

        if (!messageHandler) {
          throw new Error(`Unknown action: ${action}`);
        }

        const data = await messageHandler(payload);

        // Send response back
        window.dispatchEvent(
          new CustomEvent('dutchie-ads:response', {
            detail: { requestId, data },
          })
        );
      } catch (error) {
        // Send error back
        window.dispatchEvent(
          new CustomEvent('dutchie-ads:response', {
            detail: {
              requestId,
              error: error instanceof Error ? error.message : 'Unknown error',
            },
          })
        );
      }
    },
    [messageHandlerForAction]
  );

  useEffect(() => {
    // Listen for requests from the extension
    const handleEvent = (event: CustomEvent<AdsInspectorRequest>): void => {
      void eventHandler(event);
    };

    window.addEventListener('dutchie-ads:request', handleEvent);

    return () => {
      window.removeEventListener('dutchie-ads:request', handleEvent);
    };
  }, [eventHandler]);

  return null;
}
